import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Bell, ChatCircleDots, Check } from "@phosphor-icons/react";
import Inbox from "@/pages/Inbox";
import { useAdaptiveInterval } from "@/context/DataSaverContext";

export default function Alerts() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "inbox" ? "inbox" : "notifications";
  const setTab = (v) => setParams({ tab: v }, { replace: true });

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6" data-testid="alerts-page">
      <div className="mb-4">
        <h1 className="font-heading text-3xl md:text-4xl font-black">Activity</h1>
        <p className="text-white/60 mt-1">Notifications and direct messages in one place.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 bg-white/5">
          <TabsTrigger data-testid="alerts-tab-notifications" value="notifications" className="gap-2">
            <Bell size={16} weight="bold" /> Notifications
          </TabsTrigger>
          <TabsTrigger data-testid="alerts-tab-inbox" value="inbox" className="gap-2">
            <ChatCircleDots size={16} weight="bold" /> Inbox
          </TabsTrigger>
        </TabsList>
        <TabsContent value="notifications" className="mt-4">
          <NotificationsPanel />
        </TabsContent>
        <TabsContent value="inbox" className="mt-4">
          <Inbox />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NotificationsPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useAdaptiveInterval(load, 45000);  // was 20s → 45s

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.notif_id}/read`);
    }
    if (n.link) navigate(n.link);
    else load();
  };

  if (loading) return <div className="text-white/60 text-sm py-8 text-center">Loading…</div>;

  return (
    <Card className="bg-[#121212] border-white/10">
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <p className="text-sm text-white/60">
            {items.filter((n) => !n.read).length} unread
          </p>
          <button
            data-testid="notif-read-all"
            onClick={readAll}
            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            <Check size={14} weight="bold" /> Mark all read
          </button>
        </div>
        <div>
          {items.length === 0 ? (
            <p className="text-white/50 text-sm py-10 text-center">No notifications yet.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.notif_id}
                data-testid={`notif-${n.notif_id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left p-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] transition ${
                  !n.read ? "bg-blue-500/[0.04]" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  {!n.read && <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{n.title}</p>
                    {n.body && <p className="text-xs text-white/60 mt-0.5">{n.body}</p>}
                    <p className="text-[10px] text-white/40 mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
